/* eslint max-lines: off */
import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { OfferScope } from '../types';
import { JobsBriefActions } from './jobs/jobs-brief.store';
import { JobsFullActions } from './jobs/jobs-full.store';
import { JobsActions } from './jobs/jobs.actions';
import { JobItemsActions, JobItemsSelectors } from './jobs/jobs.store';
import { OffersBriefActions } from './offers-brief.actions';
import { OfferItemsActions } from './offers.actions';

@Injectable({
  providedIn: 'root',
})
export class JobsGlobalEffects {
  public fetchActiveCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OfferItemsActions.fetchActiveCollections),
      switchMap(() => [JobItemsActions.fetchActiveCollection()]),
    ),
  );
  public fetchArchivedCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OfferItemsActions.fetchArchivedCollections),
      switchMap(() => [JobItemsActions.fetchArchivedCollection()]),
    ),
  );
  public fetchActiveBriefCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OffersBriefActions.fetchActiveCollections),
      switchMap(() => [JobsBriefActions.fetchActiveCollection()]),
    ),
  );
  public fetchArchivedBriefCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OffersBriefActions.fetchArchivedCollections),
      switchMap(() => [JobsBriefActions.fetchArchivedCollection()]),
    ),
  );

  public updateFromFull$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.fetchOneSuccess),
      switchMap(({ payload }) => [
        JobItemsActions.upsertOne({ payload: { data: payload.offer, scope: OfferScope.Active } }),
        JobsBriefActions.upsertOne({ payload: { data: payload.offer, scope: OfferScope.Active } }),
      ]),
    ),
  );
  public updateFromItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobItemsActions.fetchCollectionSuccess),
      switchMap(({ payload }) => [
        JobsBriefActions.upsertMany({
          payload: { data: payload.data, scope: <OfferScope>payload.scope },
        }),
      ]),
    ),
  );

  public create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.create),
      switchMap(({ payload }) => of(JobsFullActions.create({ payload }))),
    ),
  );
  public createSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createSuccess),
      switchMap(({ payload }) => [JobsActions.createSuccess({ payload })]),
    ),
  );
  public createFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createFailed),
      switchMap(({ payload }) => [JobsActions.createFailed({ payload })]),
    ),
  );

  public createFromRequisition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.createFromRequisition),
      switchMap(({ payload }) => of(JobsFullActions.createFromRequisition({ payload }))),
    ),
  );
  public createFromRequisitionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createFromRequisitionSuccess),
      switchMap(({ payload }) => [JobsActions.createFromRequisitionSuccess({ payload })]),
    ),
  );
  public createFromRequisitionFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createFromRequisitionFailed),
      switchMap(({ payload }) => [JobsActions.createFromRequisitionFailed({ payload })]),
    ),
  );

  public update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.update),
      switchMap(({ payload }) => of(JobsFullActions.update({ payload }))),
    ),
  );
  public updateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateSuccess),
      switchMap(({ payload }) => [
        JobsActions.updateSuccess({ payload: { data: payload.data } }),
        JobItemsActions.upsertOne({ payload: { data: payload.data, scope: OfferScope.Active } }),
        JobsBriefActions.upsertOne({ payload: { data: payload.data, scope: OfferScope.Active } }),
      ]),
    ),
  );
  public updateFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateFailed),
      switchMap(({ payload }) => [JobsActions.updateFailed({ payload })]),
    ),
  );

  public remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.removeOne),
      switchMap(({ payload }) => [
        JobItemsActions.removeOnePessimistic({ payload: { data: payload.id } }),
      ]),
    ),
  );
  public removeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobItemsActions.removeOnePessimisticSuccess),
      switchMap(({ payload }) => [
        JobsActions.removeOneSuccess({ payload: { id: payload.data.id } }),
        JobsFullActions.removeOne({ payload: { data: payload.data.id } }),
        JobsBriefActions.removeOne({ payload: { data: payload.data.id } }),
      ]),
    ),
  );
  public removeFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobItemsActions.removeOnePessimisticFailed),
      switchMap(({ payload }) => [
        JobsActions.removeOneFailed({
          payload: { id: <number>payload.data, error: payload.error },
        }),
      ]),
    ),
  );

  public changeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.changeStatus),
      concatLatestFrom(({ payload }) => this.store.select(JobItemsSelectors.byId(payload.id))),
      switchMap(([{ payload }, jobItem]) =>
        of(
          JobsFullActions.changeStatus({
            payload: { ...payload, currentStatus: jobItem?.status ?? payload.currentStatus! },
          }),
        ),
      ),
    ),
  );
  public changeStatusSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.changeStatusSuccess),
      switchMap(({ payload }) => [
        ...(payload.isChangingScope
          ? [
              JobItemsActions.removeOne({ payload: { data: payload.offer.id } }),
              JobsBriefActions.removeOne({ payload: { data: payload.offer.id } }),
            ]
          : []),
        JobItemsActions.upsertOne({
          payload: { data: payload.offer, scope: payload.targetScope },
        }),
        JobsBriefActions.upsertOne({
          payload: { data: payload.offer, scope: payload.targetScope },
        }),
        JobsActions.changeStatusSuccess({ payload: { offer: payload.offer } }),
      ]),
    ),
  );
  public changeStatusFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.changeStatusFailed),
      switchMap(({ payload }) => [JobsActions.changeStatusFailed({ payload })]),
    ),
  );

  public updatePositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.updatePositions),
      switchMap(({ payload }) => [JobItemsActions.updatePositions({ payload })]),
    ),
  );
  public updatePositionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobItemsActions.updatePositionSuccess),
      switchMap(() => [JobsActions.updatePositionsSuccess()]),
    ),
  );
  public updatePositionsFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobItemsActions.updatePositionsFailed),
      switchMap(({ payload }) => [JobsActions.updatePositionsFailed({ payload })]),
    ),
  );

  public preview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.preview),
      switchMap(({ payload }) => [JobsFullActions.preview({ payload })]),
    ),
  );
  public previewSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.previewSuccess),
      switchMap(({ payload }) => [JobsActions.previewSuccess({ payload })]),
    ),
  );
  public previewFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.previewFailed),
      switchMap(({ payload }) => [JobsActions.previewFailed({ payload })]),
    ),
  );

  public updateSocialMedia$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.updateSocialMedia),
      switchMap(({ payload }) => [JobsFullActions.updateSocialMedia({ payload })]),
    ),
  );
  public updateSocialMediaSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateSocialMediaSuccess),
      switchMap(({ payload }) => [JobsActions.updateSocialMediaSuccess({ payload })]),
    ),
  );
  public updateSocialMediaFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateSocialMediaFailed),
      switchMap(({ payload }) => [JobsActions.updateSocialMediaFailed({ payload })]),
    ),
  );

  public updateSocialMediaImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.updateSocialMediaImage),
      switchMap(({ payload }) => [JobsFullActions.updateSocialMediaImage({ payload })]),
    ),
  );
  public updateSocialMediaImageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateSocialMediaImageSuccess),
      switchMap(({ payload }) => [JobsActions.updateSocialMediaImageSuccess({ payload })]),
    ),
  );
  public updateSocialMediaImageFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateSocialMediaImageFailed),
      switchMap(({ payload }) => [JobsActions.updateSocialMediaImageFailed({ payload })]),
    ),
  );

  public updateCoverImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.updateCoverImage),
      switchMap(({ payload }) => [JobsFullActions.updateCoverImage({ payload })]),
    ),
  );
  public updateCoverImageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateCoverImageSuccess),
      switchMap(({ payload }) => [JobsActions.updateCoverImageSuccess({ payload })]),
    ),
  );
  public updateCoverImageFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateCoverImageFailed),
      switchMap(({ payload }) => [JobsActions.updateCoverImageFailed({ payload })]),
    ),
  );

  public updateSlug$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.updateSlug),
      switchMap(({ payload }) => [JobsFullActions.updateSlug({ payload })]),
    ),
  );
  public updateSlugSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateSlugSuccess),
      switchMap(({ payload }) => [JobsActions.updateSlugSuccess({ payload })]),
    ),
  );
  public updateSlugFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateSlugFailed),
      switchMap(({ payload }) => [JobsActions.updateSlugFailed({ payload })]),
    ),
  );

  public follow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.follow),
      switchMap(({ payload }) => of(JobsFullActions.follow({ payload }))),
    ),
  );
  public followSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.followSuccess),
      switchMap(({ payload }) => [
        JobItemsActions.upsertOne({
          payload: { data: payload.job, scope: payload.scope },
        }),
        JobsBriefActions.upsertOne({
          payload: { data: payload.job, scope: payload.scope },
        }),
        JobsActions.followSuccess(),
      ]),
    ),
  );
  public followFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.followFailed),
      switchMap(({ payload }) => [JobsActions.followFailed({ payload })]),
    ),
  );

  public unfollow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.unfollow),
      switchMap(({ payload }) => of(JobsFullActions.unfollow({ payload }))),
    ),
  );
  public unfollowSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.unfollowSuccess),
      switchMap(({ payload }) => [
        JobItemsActions.upsertOne({
          payload: { data: payload.job, scope: payload.scope },
        }),
        JobsBriefActions.upsertOne({
          payload: { data: payload.job, scope: payload.scope },
        }),
        JobsActions.unfollowSuccess(),
      ]),
    ),
  );
  public unfollowFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.unfollowFailed),
      switchMap(({ payload }) => [JobsActions.unfollowFailed({ payload })]),
    ),
  );

  public duplicate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActions.duplicate),
      switchMap(({ payload }) => of(JobsFullActions.duplicate({ payload }))),
    ),
  );
  public duplicateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.duplicateSuccess),
      switchMap(({ payload }) => [
        JobsActions.duplicateSuccess({ payload: { offer: payload.offer } }),
        JobItemsActions.fetchActiveCollection(),
      ]),
    ),
  );
  public duplicateFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.duplicateFailed),
      switchMap(({ payload }) => [JobsActions.duplicateFailed({ payload })]),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
  ) {}
}
